import { useCurrentSite } from "../../../contexts/models/SiteContext";
import {
  Configuration,
  Section,
  StripeRegistryDetails,
} from "../../../resources/modelTypes";
import { selectors } from "../../cypressSelectors";
import { FormFieldWrapper } from "../../shared-components/FormFieldWrapper";
import { StripeRegistryProgressBar } from "../../shared-components/StripeRegistryProgressBar";
import { formatDollars } from "../../stripeUtils";
import { DisplayPhoto } from "../DisplayPhoto";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import { css } from "@emotion/react";
import {
  Button,
  Icon,
  Modal,
  styleUtils,
  Textarea,
  TextInput,
  theme,
} from "@minted/minted-components";
import React, { memo, useCallback, useState } from "react";

// Placeholder until a funded-total endpoint exists (same pattern as StripeRegistryManagePreview)
const PLACEHOLDER_FUNDED_CENTS = 0;

enum ContributionStep {
  Details,
  GiverInfo,
  Checkout,
  ThankYou,
}

type StripeContributionModalProps = {
  configuration: Configuration;
  onClose: () => void;
  section: Section;
};

const styles = {
  description: css`
    ${theme.typeStyles.bodySmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
  `,
  disclaimer: css`
    ${theme.typeStyles.bodyExtraSmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x3};
    text-align: center;
  `,
  fixedAmountText: css`
    ${theme.typeStyles.bodySmallStyles}
    font-weight: 500;
  `,
  formFieldsStack: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    margin-top: ${theme.spacing.x4};
  `,
  giftAmountInput: css`
    margin-top: ${theme.spacing.x4};
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    border-radius: ${styleUtils.rem(4)};
    height: ${styleUtils.rem(304)};
    overflow: hidden;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  layout: css`
    display: grid;
    gap: ${theme.spacing.x6};
    grid-template-columns: 1fr 1fr;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      grid-template-columns: 1fr;
    `)}
  `,
  nextButton: css`
    margin-top: ${theme.spacing.x6};
  `,
  rightColumn: css`
    display: flex;
    flex-direction: column;
  `,
  thankYouButtonWrapper: css`
    width: 100%;
  `,
  thankYouSubtext: css`
    ${theme.typeStyles.bodyMediumStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
  `,
  thankYouWrapper: css`
    max-width: ${styleUtils.rem(370)};
    justify-self: center;
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
    padding-bottom: ${theme.spacing.x10};
    padding-left: ${theme.spacing.x8};
    padding-right: ${theme.spacing.x8};
    padding-top: ${theme.spacing.x10};
    text-align: center;
  `,
  title: css`
    ${theme.typeStyles.titleExtraLargeStyles}
  `,
};

const StripeContributionModal = ({
  configuration,
  onClose,
  section,
}: StripeContributionModalProps) => {
  const site = useCurrentSite();
  const siteDetails = site.siteDetails as
    | { yourName?: string; spouseName?: string }
    | undefined;
  const coupleName = [siteDetails?.yourName, siteDetails?.spouseName]
    .filter(Boolean)
    .join(" & ");

  const details = section.sectionDetails as StripeRegistryDetails;

  const [step, setStep] = useState<ContributionStep>(ContributionStep.Details);
  const [selectedAmountCents, setSelectedAmountCents] = useState<number | null>(
    details.isFixedAmount ? details.giftAmountCents : null,
  );
  const [giverName, setGiverName] = useState("");
  const [giverEmail, setGiverEmail] = useState("");
  const [note, setNote] = useState("");

  const onOtherAmountChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!/^\d*$/.test(value)) return;
      setSelectedAmountCents(value ? parseInt(value, 10) * 100 : null);
    },
    [],
  );

  const onGiverNameChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setGiverName(value),
    [],
  );

  const onGiverEmailChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setGiverEmail(value),
    [],
  );

  const onNoteChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) =>
      setNote(value),
    [],
  );

  const isStep1Valid = selectedAmountCents !== null && selectedAmountCents > 0;
  const isStep2Valid =
    giverName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giverEmail.trim());

  const renderImage = useCallback(
    () => (
      <div css={styles.imageWrapper}>
        <DisplayPhoto
          configuration={configuration}
          defaultImage={CashRegistryDefaultImage}
          isRegistryPhoto
          section={section}
        />
      </div>
    ),
    [configuration, section],
  );

  const renderStep1 = useCallback(
    () => (
      <div
        css={styles.layout}
        data-cy={selectors.stripeRegistry_contributionModal_step1}
      >
        {renderImage()}
        <div css={styles.rightColumn}>
          <div css={styles.title}>{details.title}</div>
          {details.textDescription && (
            <div css={styles.description}>{details.textDescription}</div>
          )}
          {!details.hideGoal && (
            <StripeRegistryProgressBar
              fundedCents={PLACEHOLDER_FUNDED_CENTS}
              goalAmountCents={details.goalAmountCents}
            />
          )}
          <div css={styles.giftAmountInput}>
            <FormFieldWrapper label="Gift Amount">
              {details.isFixedAmount ? (
                <div css={styles.fixedAmountText}>
                  {formatDollars(details.giftAmountCents)}
                </div>
              ) : (
                <TextInput
                  addons={[{ align: "left", content: "$" }]}
                  data-cy={
                    selectors.stripeRegistry_contributionModal_amountOtherInput
                  }
                  inputMode="numeric"
                  name="giftAmount"
                  onChange={onOtherAmountChange}
                  pattern="[0-9]*"
                  placeholder="Enter amount"
                  touched
                  value={
                    selectedAmountCents !== null
                      ? String(selectedAmountCents / 100)
                      : ""
                  }
                />
              )}
            </FormFieldWrapper>
          </div>
          <div
            css={styles.nextButton}
            data-cy={selectors.stripeRegistry_contributionModal_nextButton}
          >
            <Button
              disabled={!isStep1Valid}
              expand
              onClick={() => setStep(ContributionStep.GiverInfo)}
              text="Next: Add your details"
              type={Button.types.primary}
            />
          </div>
        </div>
      </div>
    ),
    [
      details,
      isStep1Valid,
      onOtherAmountChange,
      selectedAmountCents,
      renderImage,
    ],
  );

  const renderStep2 = useCallback(
    () => (
      <div
        css={styles.layout}
        data-cy={selectors.stripeRegistry_contributionModal_step2}
      >
        {renderImage()}
        <div css={styles.rightColumn}>
          <div css={styles.title}>{details.title}</div>
          <div css={styles.description}>
            {`Share your details so ${coupleName} can track your gift`}
          </div>
          <div css={styles.formFieldsStack}>
            <FormFieldWrapper label="Name">
              <TextInput
                data-cy={selectors.stripeRegistry_contributionModal_nameInput}
                name="giverName"
                onChange={onGiverNameChange}
                placeholder="First and last name"
                touched
                value={giverName}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Email">
              <TextInput
                data-cy={selectors.stripeRegistry_contributionModal_emailInput}
                inputMode="email"
                name="giverEmail"
                onChange={onGiverEmailChange}
                placeholder="Email"
                touched
                value={giverEmail}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Add a note for the couple (optional)">
              <Textarea
                data-cy={selectors.stripeRegistry_contributionModal_noteInput}
                minRows={3}
                name="note"
                onChange={onNoteChange}
                placeholder="Write a personal note"
                value={note}
              />
            </FormFieldWrapper>
          </div>
          <div
            css={styles.nextButton}
            data-cy={selectors.stripeRegistry_contributionModal_nextButton}
          >
            <Button
              disabled={!isStep2Valid}
              expand
              iconType={Icon.types["open-in-new"]}
              onClick={() => setStep(ContributionStep.Checkout)}
              text={`Next: Give ${formatDollars(selectedAmountCents ?? 0)}`}
              type={Button.types.primary}
            />
          </div>
          <div css={styles.disclaimer}>
            Minted doesn&apos;t process payments. You&apos;ll complete your gift
            securely on Stripe.
          </div>
        </div>
      </div>
    ),
    [
      coupleName,
      details,
      giverEmail,
      giverName,
      isStep2Valid,
      note,
      onGiverEmailChange,
      onGiverNameChange,
      onNoteChange,
      renderImage,
      selectedAmountCents,
    ],
  );

  const renderStep3 = useCallback(
    () => (
      <div data-cy={selectors.stripeRegistry_contributionModal_step3}>
        {/*
         * TODO: Implement embedded Stripe checkout here.
         *
         * Prerequisites:
         *   1. Add `@stripe/react-stripe-js` as a package dependency (via
         *      Dockerfile / make targets — do NOT run yarn directly on the host).
         *   2. Call API.post("stripe/payment-intent/", {
         *        sectionId: section.id,
         *        amountCents: selectedAmountCents,
         *        giverName,
         *        giverEmail,
         *        note,
         *      }) to obtain a `clientSecret`.
         *   3. Use loadStripe(config.get("stripePublishableKey")) → <Elements
         *        stripe={stripePromise} options={{ clientSecret }}>.
         *   4. Render <PaymentElement> with billing_details pre-filled from giverName/giverEmail.
         *   5. On stripe.confirmPayment() success, call setStep(ContributionStep.ThankYou).
         */}
        <p>Payment coming soon.</p>
      </div>
    ),
    [],
  );

  const renderStep4 = useCallback(
    () => (
      <div
        css={styles.thankYouWrapper}
        data-cy={selectors.stripeRegistry_contributionModal_step4}
      >
        <Icon size={Icon.sizes.large} type={Icon.types.gift} />
        <div>
          <div css={styles.title}>Thank you!</div>
          <div css={styles.thankYouSubtext}>
            {`We've let ${coupleName} know you sent this gift with Stripe`}
          </div>
        </div>
        <div
          css={styles.thankYouButtonWrapper}
          data-cy={selectors.stripeRegistry_contributionModal_doneButton}
        >
          <Button
            expand
            onClick={onClose}
            text="Done"
            type={Button.types.primary}
          />
        </div>
      </div>
    ),
    [coupleName, onClose],
  );

  const renderContent = useCallback(() => {
    switch (step) {
      case ContributionStep.Details:
        return renderStep1();
      case ContributionStep.GiverInfo:
        return renderStep2();
      case ContributionStep.Checkout:
        return renderStep3();
      case ContributionStep.ThankYou:
        return renderStep4();
    }
  }, [renderStep1, renderStep2, renderStep3, renderStep4, step]);

  return (
    <Modal
      closeOnEsc
      closeOnOverlayClick
      displayCloseButton
      onCloseClick={onClose}
      open
      size={Modal.sizes.medium}
    >
      <Modal.Body>{renderContent()}</Modal.Body>
    </Modal>
  );
};

const MemoizedStripeContributionModal = memo(StripeContributionModal);

export { MemoizedStripeContributionModal as StripeContributionModal };
